<script setup lang="ts">
import { ref, nextTick, onBeforeUnmount } from 'vue'

const LOOP_INTERVAL = 11000 // 10s animation + 1s pause

const show = ref(false)
const sceneRef = ref<HTMLElement>()
let loopTimer: ReturnType<typeof setInterval> | null = null

const playAnimation = () => {
  const scene = sceneRef.value
  if (!scene) return
  scene.classList.remove('playing')
  void scene.offsetWidth
  scene.classList.add('playing')
}

const startLoop = () => {
  stopLoop()
  playAnimation()
  loopTimer = setInterval(playAnimation, LOOP_INTERVAL)
}

const stopLoop = () => {
  if (loopTimer) {
    clearInterval(loopTimer)
    loopTimer = null
  }
}

const open = () => {
  show.value = true
  nextTick(() => {
    startLoop()
  })
}

const close = () => {
  stopLoop()
  show.value = false
}

onBeforeUnmount(() => {
  stopLoop()
})

defineExpose({ open })
</script>

<template>
  <div v-if="show" class="modal-overlay" @click.self="close">
    <div class="modal-card">
      <div class="modal-header">
        <span class="modal-title">使い方</span>
        <button class="modal-close" @click="close">
          <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
            <path d="M4 4l8 8M12 4l-8 8" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
          </svg>
        </button>
      </div>
      <div class="modal-body">
        <p class="steps-summary">候補日時のテキストを貼り付けるだけで、空き確認〜カレンダー追加まで完了します。</p>
        <div class="scene" ref="sceneRef">
          <!-- Textarea phase -->
          <div class="textarea-area">
            <div class="textarea-mock">
              <div class="placeholder">受け取った候補日時のテキストを貼り付け...</div>
              <div class="scan-highlight"></div>
              <div class="paste-badge">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="9" y="2" width="6" height="4" rx="1"/><path d="M8 4H6a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V6a2 2 0 0 0-2-2h-2"/></svg>
                ペースト
              </div>
              <div class="text-content">
                <div class="text-line">お世話になっております。</div>
                <div class="text-line">下記日程でご都合いかがでしょうか。</div>
                <div class="text-line spacer"></div>
                <div class="text-line">・4月7日（月）14:00〜15:00</div>
                <div class="text-line">・4月9日（水）10:00〜11:30</div>
                <div class="text-line">・4月11日（金）16:00〜17:00</div>
              </div>
            </div>
          </div>

          <!-- Arrow -->
          <div class="extract-arrow">
            <span class="arrow-icon">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#429F9F" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M12 5v14"/><path d="m19 12-7 7-7-7"/></svg>
            </span>
            <span class="extract-label">自動読み取り</span>
          </div>

          <!-- Extracted cards -->
          <div class="cards-area">
            <div class="date-card free target">
              <div class="date-info">
                <span class="date-label">4月7日（月）14:00〜15:00</span>
              </div>
              <span class="chip free">空き ○</span>
              <button class="cal-btn" tabindex="-1">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
              </button>
            </div>
            <div class="date-card busy">
              <div class="date-info">
                <span class="date-label">4月9日（水）10:00〜11:30</span>
              </div>
              <span class="chip busy">A社定例 ×</span>
            </div>
            <div class="date-card free">
              <div class="date-info">
                <span class="date-label">4月11日（金）16:00〜17:00</span>
              </div>
              <span class="chip free">空き ○</span>
              <button class="cal-btn" tabindex="-1">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
              </button>
            </div>
          </div>

          <!-- Fake cursor -->
          <svg class="fake-cursor" viewBox="0 0 24 24">
            <path d="M5 3l14 8-6.5 2L9 19.5z" fill="#333" stroke="white" stroke-width="1"/>
          </svg>

          <!-- Overlay -->
          <div class="cal-overlay"></div>

          <!-- Calendar popup -->
          <div class="cal-popup">
            <div class="cal-popup-header">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
              Google カレンダーに追加
            </div>
            <div class="cal-popup-body">
              <div class="cal-popup-title">打ち合わせ</div>
              <div class="cal-popup-detail">
                &#128197; 4月7日（月）<br>
                &#128336; 14:00 〜 15:00
              </div>
            </div>
          </div>

          <!-- Success toast -->
          <div class="success-toast">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
            カレンダーに追加しました
          </div>
        </div>

      </div>
    </div>
  </div>
</template>

<style scoped>
/* ===== Modal ===== */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.3);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 300;
}
.modal-card {
  background: #FAF7F4;
  border-radius: 12px;
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.15);
  width: 480px;
  max-width: calc(100vw - 32px);
  max-height: calc(100vh - 64px);
  overflow: hidden;
  display: flex;
  flex-direction: column;
}
.modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 16px 10px;
}
.modal-title {
  font-size: 0.875rem;
  font-weight: 600;
  color: var(--font-black);
}
.modal-close {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border: none;
  background: none;
  color: var(--font-gray-3);
  cursor: pointer;
  border-radius: 4px;
}
.modal-close:hover {
  background: #f3f4f6;
}
.modal-body {
  padding: 0 16px 16px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  overflow-y: auto;
}
.steps-summary {
  width: 100%;
  margin: 0;
  font-size: 0.8125rem;
  color: var(--font-gray-2, rgba(41, 27, 15, 0.6));
  line-height: 1.6;
}

/* ===== Scene ===== */
.scene {
  width: 100%;
  display: flex;
  flex-direction: column;
  padding: 16px;
  position: relative;
  background: #FAF7F4;
  border-radius: 8px;
}

/* ===== Textarea ===== */
.textarea-area {
  width: 100%;
  overflow: hidden;
}
.textarea-mock {
  width: 100%;
  background: #FAF9F8;
  border: 1px solid #EFEBE9;
  border-radius: 6px;
  padding: 12px 14px;
  font-size: 12px;
  line-height: 1.7;
  color: #333;
  position: relative;
  overflow: hidden;
  min-height: 100%;
}
.textarea-mock .placeholder {
  color: rgba(41, 27, 15, 0.38);
  position: absolute;
  top: 12px;
  left: 14px;
  opacity: 1;
  font-size: 12px;
}
.textarea-mock .text-content {
  position: relative;
  z-index: 1;
  opacity: 0;
}
.text-line {
  opacity: 0;
}
.text-line.spacer {
  height: 4px;
}
.textarea-mock::after {
  content: '';
  position: absolute;
  inset: 0;
  background: #429F9F;
  opacity: 0;
  border-radius: 5px;
  pointer-events: none;
}

/* ===== Paste badge ===== */
.paste-badge {
  position: absolute;
  top: 45%;
  left: 50%;
  transform: translate(-50%, -50%) scale(0.8);
  background: #333;
  color: white;
  font-size: 12px;
  font-weight: 500;
  padding: 5px 14px;
  border-radius: 6px;
  z-index: 10;
  display: flex;
  align-items: center;
  gap: 6px;
  box-shadow: 0 4px 12px rgba(0,0,0,0.15);
  opacity: 0;
}

/* ===== Scan highlight ===== */
.scan-highlight {
  position: absolute;
  left: 0;
  right: 0;
  height: 16px;
  background: linear-gradient(90deg, transparent, rgba(66, 159, 159, 0.15), transparent);
  border-radius: 2px;
  opacity: 0;
  z-index: 2;
}

/* ===== Arrow ===== */
.extract-arrow {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 6px 0;
  opacity: 0;
  flex-shrink: 0;
}
.arrow-icon { color: #429F9F; }
.extract-label {
  font-size: 10px;
  color: #429F9F;
  font-weight: 500;
  margin-left: 6px;
}

/* ===== Cards ===== */
.cards-area {
  display: flex;
  flex-direction: column;
  gap: 6px;
  width: 100%;
  flex-shrink: 0;
  position: relative;
}
.date-card {
  width: 100%;
  padding: 8px 10px;
  border-radius: 6px;
  border: 1px solid #e5e7eb;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  opacity: 0;
  transform: translateY(8px);
}
.date-card.free { background: #f4f9ee; }
.date-card.busy { background: #fdf2f2; }
.date-card .date-info {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
  flex: 1;
}
.date-card .date-label {
  font-size: 13px;
  font-weight: 500;
  color: #333;
}
.chip {
  padding: 2px 8px;
  border-radius: 10px;
  font-size: 11px;
  font-weight: 500;
  white-space: nowrap;
  flex-shrink: 0;
  opacity: 0;
  transform: scale(0.8);
}
.chip.free { background: #EAF3DE; color: #3B6D11; }
.chip.busy { background: #FCEBEB; color: #A32D2D; }
.cal-btn {
  width: 26px;
  height: 26px;
  border: none;
  border-radius: 6px;
  background: none;
  color: rgba(41, 27, 15, 0.38);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  pointer-events: none;
}

/* ===== Cursor / Overlay / Popup / Toast ===== */
.fake-cursor {
  position: absolute;
  width: 16px;
  height: 20px;
  z-index: 20;
  opacity: 0;
  filter: drop-shadow(0 1px 2px rgba(0,0,0,0.2));
  pointer-events: none;
}
.cal-overlay {
  position: absolute;
  inset: 0;
  background: rgba(0,0,0,0.06);
  z-index: 10;
  opacity: 0;
  pointer-events: none;
  border-radius: 8px;
}
.cal-popup {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%) scale(0.92);
  width: 240px;
  background: white;
  border-radius: 8px;
  box-shadow: 0 8px 32px rgba(0,0,0,0.12);
  z-index: 15;
  opacity: 0;
  overflow: hidden;
  pointer-events: none;
}
.cal-popup-header {
  background: #429F9F;
  color: white;
  padding: 8px 14px;
  font-size: 11px;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 6px;
}
.cal-popup-body { padding: 12px 14px; }
.cal-popup-title {
  font-size: 13px;
  font-weight: 700;
  color: #333;
  margin-bottom: 6px;
}
.cal-popup-detail {
  font-size: 11px;
  color: rgba(41, 27, 15, 0.6);
  line-height: 1.6;
}
.success-toast {
  position: absolute;
  bottom: 16px;
  left: 50%;
  transform: translateX(-50%) translateY(10px);
  background: #429F9F;
  color: white;
  font-size: 12px;
  font-weight: 500;
  padding: 6px 16px;
  border-radius: 6px;
  opacity: 0;
  display: flex;
  align-items: center;
  gap: 6px;
  box-shadow: 0 4px 12px rgba(66, 159, 159, 0.3);
  white-space: nowrap;
  z-index: 20;
}

/* =======================================
   Playing state animations
   Timeline (total ~10s):
     Phase 1: Paste (0s - 1.6s)
     Phase 2: Shrink + Scan (1.8s - 3.2s)
     Phase 3: Cards appear (3.2s - 5.0s)
     Phase 4: Cursor + Calendar (5.5s - 9.5s)
   ======================================= */

/* Phase 1: Paste */
.scene.playing .paste-badge {
  animation: htu-paste-pop 1.6s 0.2s forwards;
}
.scene.playing .placeholder {
  animation: htu-fade-out 0.2s 1.0s forwards;
}
.scene.playing .text-content {
  animation: htu-fade-in 0.1s 1.0s forwards;
}
.scene.playing .text-line {
  animation: htu-fade-in 0.01s 1.0s forwards;
}
.scene.playing .textarea-mock::after {
  animation: htu-highlight-flash 0.6s 1.0s forwards;
}

/* Phase 2: Shrink + Scan */
.scene.playing .textarea-area {
  animation: htu-textarea-shrink 0.5s 1.8s cubic-bezier(0.4, 0, 0.2, 1) forwards;
}
.scene.playing .scan-highlight {
  animation: htu-scan-line 1.4s 2.0s forwards;
}

/* Phase 3: Arrow + Cards */
.scene.playing .extract-arrow {
  animation: htu-arrow-appear 0.3s 3.2s forwards;
}
.scene.playing .arrow-icon {
  animation: htu-arrow-bounce 1s 3.5s 2;
}
.scene.playing .date-card:nth-child(1) {
  animation: htu-card-slide-in 0.35s 3.5s cubic-bezier(0.16, 1, 0.3, 1) forwards;
}
.scene.playing .date-card:nth-child(2) {
  animation: htu-card-slide-in 0.35s 3.75s cubic-bezier(0.16, 1, 0.3, 1) forwards;
}
.scene.playing .date-card:nth-child(3) {
  animation: htu-card-slide-in 0.35s 4.0s cubic-bezier(0.16, 1, 0.3, 1) forwards;
}
.scene.playing .date-card:nth-child(1) .chip {
  animation: htu-chip-pop 0.25s 3.75s cubic-bezier(0.34, 1.56, 0.64, 1) forwards;
}
.scene.playing .date-card:nth-child(2) .chip {
  animation: htu-chip-pop 0.25s 4.0s cubic-bezier(0.34, 1.56, 0.64, 1) forwards;
}
.scene.playing .date-card:nth-child(3) .chip {
  animation: htu-chip-pop 0.25s 4.25s cubic-bezier(0.34, 1.56, 0.64, 1) forwards;
}

/* Phase 4: Calendar Add */
.scene.playing .fake-cursor {
  animation: htu-cursor-move 4s 5.5s forwards;
}
.scene.playing .date-card.target {
  animation: htu-card-slide-in 0.35s 3.5s cubic-bezier(0.16, 1, 0.3, 1) forwards, htu-card-hover 4s 5.5s forwards;
}
.scene.playing .target .cal-btn {
  animation: htu-btn-highlight 4s 5.5s forwards;
}
.scene.playing .cal-overlay {
  animation: htu-overlay-show 4s 5.5s forwards;
}
.scene.playing .cal-popup {
  animation: htu-popup-show 4s 5.5s forwards;
}
.scene.playing .success-toast {
  animation: htu-toast-show 4s 5.5s forwards;
}

/* ===== Keyframes ===== */
@keyframes htu-fade-out { to { opacity: 0; } }
@keyframes htu-fade-in { to { opacity: 1; } }

@keyframes htu-paste-pop {
  0% { opacity: 0; transform: translate(-50%, -50%) scale(0.8); }
  12% { opacity: 1; transform: translate(-50%, -50%) scale(1); }
  55% { opacity: 1; transform: translate(-50%, -50%) scale(1); }
  75% { opacity: 0; transform: translate(-50%, -50%) scale(0.95); }
  100% { opacity: 0; }
}

@keyframes htu-highlight-flash {
  0% { opacity: 0.12; }
  100% { opacity: 0; }
}

@keyframes htu-textarea-shrink {
  0% { max-height: 400px; }
  100% { max-height: 130px; }
}

@keyframes htu-scan-line {
  0% { opacity: 0; top: 6px; }
  8% { opacity: 1; top: 6px; }
  25% { top: 20px; }
  50% { top: 40px; }
  75% { top: 60px; }
  90% { top: 72px; opacity: 1; }
  100% { top: 72px; opacity: 0; }
}

@keyframes htu-arrow-appear {
  0% { opacity: 0; transform: translateY(-4px); }
  100% { opacity: 1; transform: translateY(0); }
}

@keyframes htu-arrow-bounce {
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(3px); }
}

@keyframes htu-card-slide-in {
  0% { opacity: 0; transform: translateY(8px); }
  100% { opacity: 1; transform: translateY(0); }
}

@keyframes htu-chip-pop {
  0% { opacity: 0; transform: scale(0.8); }
  100% { opacity: 1; transform: scale(1); }
}

@keyframes htu-cursor-move {
  0% { opacity: 0; bottom: 10%; right: 20%; }
  10% { opacity: 1; bottom: 10%; right: 20%; }
  30% { opacity: 1; bottom: 120px; right: 22px; }
  38%, 42% { opacity: 1; bottom: 120px; right: 22px; transform: scale(1); }
  44% { transform: scale(0.85); }
  46% { transform: scale(1); }
  60% { opacity: 1; bottom: 120px; right: 22px; }
  72% { opacity: 0; bottom: 120px; right: 22px; }
  100% { opacity: 0; }
}

@keyframes htu-card-hover {
  0%, 20% { box-shadow: none; }
  28%, 55% { box-shadow: 0 0 0 1px rgba(66,159,159,0.13); }
  65%, 100% { box-shadow: none; }
}

@keyframes htu-btn-highlight {
  0%, 25% { color: rgba(41, 27, 15, 0.38); background: none; }
  30%, 40% { color: #429F9F; background: #EEF5F5; }
  44% { color: #429F9F; background: #D1EBEB; }
  46%, 55% { color: #429F9F; background: #EEF5F5; }
  65%, 100% { color: rgba(41, 27, 15, 0.38); background: none; }
}

@keyframes htu-overlay-show {
  0%, 42% { opacity: 0; }
  46% { opacity: 1; }
  68% { opacity: 1; }
  75% { opacity: 0; }
  100% { opacity: 0; }
}

@keyframes htu-popup-show {
  0%, 42% { opacity: 0; transform: translate(-50%, -50%) scale(0.92); }
  46% { opacity: 1; transform: translate(-50%, -50%) scale(1); }
  68% { opacity: 1; transform: translate(-50%, -50%) scale(1); }
  75% { opacity: 0; transform: translate(-50%, -50%) scale(0.97); }
  100% { opacity: 0; }
}

@keyframes htu-toast-show {
  0%, 45% { opacity: 0; transform: translateX(-50%) translateY(10px); }
  50% { opacity: 1; transform: translateX(-50%) translateY(0); }
  70% { opacity: 1; transform: translateX(-50%) translateY(0); }
  78% { opacity: 0; transform: translateX(-50%) translateY(-4px); }
  100% { opacity: 0; }
}
</style>
